import React, { useState, forwardRef } from 'react';
import { Modal, Form, Input, Radio, Button, message } from 'antd';
import { useAuth } from '../../../auth/AuthContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReactDatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import InputMask from 'react-input-mask';
import './AddPatientModal.css'

// Custom input component that applies an input mask for YYYY-MM-DD format
const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
  <InputMask
    mask="9999-99-99"
    value={value || ''}
    onChange={onChange}
    onClick={onClick}
    placeholder={placeholder}
    ref={ref}
  >
    {(inputProps) => <input {...inputProps} />}
  </InputMask>
));

// DatePickerField is a controlled component that works with antd Form's value and onChange
const DatePickerField = ({ value, onChange }) => {
  return (
    <ReactDatePicker
      selected={value}
      onChange={(date) => onChange(date)}
      dateFormat="yyyy-MM-dd"
      placeholderText="YYYY-MM-DD"
      customInput={<CustomDateInput />}
    />
  );
};

const AddPatientModal = ({
  open,
  setOpen,
  fetchPatients,
  setPatientId,
  setPatientName,
  setUpdated,
}) => {
  const auth = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userDetails = localStorage.getItem('userDetails');
  const user = userDetails && JSON.parse(userDetails);

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setLoading(true);

        // Trim all string values to remove extra spaces at start or end
        const formattedValues = {
          ...values,
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          email: values.email ? values.email.trim() : null,
          phone: values.phone ? values.phone.trim() : null,
          // Convert the Date object to desired format using moment
          date_of_birth: values.date_of_birth
            ? moment(values.date_of_birth).format('YYYY-MM-DD')
            : null,
          notes: values.quick_patient_notes
            ? values.quick_patient_notes.trim()
            : null,
        };

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/patients/create`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${auth.user['access_token']}`,
              },
              method: 'POST',
              body: JSON.stringify(formattedValues),
            }
          );

          if (response.ok) {
            const result = await response.json();
            setUpdated && setUpdated(true);
            fetchPatients && (await fetchPatients());
            setPatientId && setPatientId(result.patient_id);
            setPatientName &&
              setPatientName(
                `${formattedValues.first_name} ${formattedValues.last_name}`
              );
            message.success('Patient added successfully');
            form.resetFields(); // Reset the form fields after submission
          } else {
            const errorData = await response.json();
            message.error(
              errorData.detail?.message || 'Failed to add patient information'
            );
          }

          setOpen(false); // Close the modal
        } catch (error) {
          message.error('Failed to add patient');
          console.error('Error submitting patient data:', error);
        } finally {
          setLoading(false);
        }
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
        setLoading(false);
      });
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  return (
    <Modal
      title="Add a New Patient"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      maskClosable={false}
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" name="add_patient_form">
        <Form.Item
          name="first_name"
          label="First Name"
          rules={[{ required: true, message: 'Please input the first name!' }]}
        >
          <Input placeholder="Enter first name" />
        </Form.Item>

        <Form.Item
          name="last_name"
          label="Last Name"
          rules={[{ required: true, message: 'Please input the last name!' }]}
        >
          <Input placeholder="Enter last name" />
        </Form.Item>

        <Form.Item name="date_of_birth" label="Date of Birth (YYYY-MM-DD)">
          {/* Use DatePickerField so that Form receives value and onChange */}
          <DatePickerField />
        </Form.Item>

        <Form.Item name="gender" label="Gender">
          <Radio.Group>
            <Radio value="M">Male</Radio>
            <Radio value="F">Female</Radio>
            <Radio value="O">Other</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="email" label="Email">
          <Input placeholder="Enter email" />
        </Form.Item>

        <Form.Item name="phone" label="Phone">
          <PhoneInput
            country={'us'}
            value={form.getFieldValue('phone')}
            onChange={(value) => form.setFieldsValue({ phone: value.trim() })}
            inputStyle={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="quick_patient_notes" label="Quick Patient Notes">
          <Input.TextArea
            placeholder="Enter any quick notes about the patient"
            rows={4}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddPatientModal;
